import { loadRemote } from "@module-federation/enhanced/runtime";
import type { AppModule, AppMountContext, NavigationOptions, PlatformBus } from "@tc-superpods/app-contract";
import type { Dispatch } from "react";
import {
  createIsolatedMountPoint,
  isAppModule,
  registerRemote,
  renderAppHost,
  renderShellError
} from "./app-host";
import type { DialogController } from "./dialog-controller";
import { createMountContext } from "./mount-context";
import { createRemoteStyleBoundary, type RemoteStyleBoundary } from "../remote-style-boundary";
import { normalizePathname } from "./config";
import { appendLocationSuffix, getCurrentAppPath, resolveEntry, resolveInitialEntry, resolveWorkspaceContext } from "./registry";
import type { ShellAction } from "./state";
import type { ActiveApp, AppStatus, RegistryEntry, TenantSummary, Workspace } from "./types";

type TimelineLogger = (step: string, details?: Record<string, unknown>) => void;

export interface RouterSession {
  bus: PlatformBus;
  identity: AppMountContext["identity"];
  tenant: AppMountContext["tenant"];
}

export interface RouterDeps {
  dispatch: Dispatch<ShellAction>;
  getOutlet: () => HTMLElement | null;
  getSession: () => RouterSession;
  getAccessibleTenants: () => ReadonlyArray<TenantSummary>;
  getBrowserPath: (route: string, workspaceId?: string | null) => string;
  getRegistry: () => RegistryEntry[];
  getWorkspaces: () => ReadonlyArray<Workspace>;
  getActiveWorkspaceId: () => string | null;
  onWorkspaceResolved: (workspaceId: string | null) => void;
  getVariables: () => Record<string, string>;
  onNoApps: () => Promise<void>;
  dialogController: DialogController;
  log?: TimelineLogger;
}

export interface Router {
  href: (to: string, options?: NavigationOptions) => string;
  navigateTo: (to: string, options?: NavigationOptions) => void;
  handlePopState: () => Promise<void>;
  start: () => Promise<void>;
  clearActiveApp: () => Promise<void>;
}

export function createRouter(deps: RouterDeps): Router {
  const {
    dispatch,
    getOutlet,
    getSession,
    getAccessibleTenants,
    getBrowserPath,
    getRegistry,
    getWorkspaces,
    getActiveWorkspaceId,
    onWorkspaceResolved,
    getVariables,
    onNoApps,
    dialogController,
    log
  } = deps;

  type RouteSource = "navigate" | "popstate";

  let activeApp: ActiveApp | null = null;
  let status: AppStatus = "not-loaded";
  let queue: Promise<void> = Promise.resolve();
  // Set to true before dispatching a synthetic popstate for intra-app navigation
  // so the shell's own popstate listener skips that event and does not re-trigger runRoute.
  let suppressNextPopState = false;

  function href(to: string, options?: NavigationOptions): string {
    return getBrowserPath(to, options?.workspaceId);
  }

  function navigateTo(to: string, options?: NavigationOptions): void {
    const destination = href(to, options);
    log?.("navigation:push-state", {
      destination,
      input: to
    });
    window.history.pushState(null, "", destination);
    void scheduleRoute();
  }

  function handlePopState(): Promise<void> {
    if (suppressNextPopState) {
      suppressNextPopState = false;
      return Promise.resolve();
    }
    return scheduleRoute("popstate");
  }

  function start(): Promise<void> {
    return scheduleRoute();
  }

  function clearActiveApp(): Promise<void> {
    queue = queue.then(() => unmountActiveApp());
    return queue;
  }

  function scheduleRoute(source: RouteSource = "navigate"): Promise<void> {
    queue = queue.then(() => runRoute(source));
    return queue;
  }

  async function runRoute(source: RouteSource = "navigate"): Promise<void> {
    const outlet = getOutlet();

    if (!outlet) {
      return;
    }

    const pathname = window.location.pathname;
    const registry = getRegistry();
    const rawAppPath = getCurrentAppPath(pathname, getAccessibleTenants());
    let appPath = rawAppPath;
    let pendingHostElement: HTMLElement | null = null;
    let pendingStyles: RemoteStyleBoundary | null = null;

    log?.("route-render:start", {
      appPath,
      tenantId: getSession().tenant.id
    });

    try {
      dispatch({ type: "set-registry", registry });

      if (appPath === "/") {
        dispatch({ type: "set-current-entry", entry: null });
        const initialEntry = resolveInitialEntry(getWorkspaces());
        if (!initialEntry) {
          await unmountActiveApp();
          await onNoApps();
          return;
        }
        redirectToInitialRoute(initialEntry.route);
        return;
      }

      const resolvedWorkspace = resolveWorkspaceContext(
        rawAppPath,
        getWorkspaces(),
        registry,
        getActiveWorkspaceId()
      );
      onWorkspaceResolved(resolvedWorkspace.workspaceId);
      appPath = resolvedWorkspace.appPath;
      const activeWorkspace = getWorkspaces().find((workspace) => workspace.id === resolvedWorkspace.workspaceId) ?? null;

      const firstRawSegment = rawAppPath.slice(1).split("/")[0];
      if (resolvedWorkspace.workspaceId && firstRawSegment !== resolvedWorkspace.workspaceId) {
        // No workspace segment in the URL yet (old bookmark, first visit) — the
        // workspace was resolved by fallback, so normalize the URL to include it.
        const normalizedDestination = appendLocationSuffix(getBrowserPath(appPath), window.location.search, window.location.hash);
        if (normalizedDestination !== `${pathname}${window.location.search}${window.location.hash}`) {
          window.history.replaceState(window.history.state, "", normalizedDestination);
        }
      }

      const entry = resolveEntry(registry, appPath);

      // Same app already mounted at a different sub-route — skip unmount/remount.
      // For push-state navigation, dispatch a synthetic popstate so the app's
      // internal router (React Router etc.) picks up the new URL. The suppress
      // flag prevents the shell's own popstate listener from re-triggering this.
      // For real popstate navigation (back/forward), React Router already responded
      // to the original event; no synthetic dispatch needed.
      if (
        activeApp &&
        entry &&
        activeApp.entry.id === entry.id &&
        activeApp.workspaceId === resolvedWorkspace.workspaceId
      ) {
        log?.("route-render:same-app-navigate", { appId: entry.id, appPath });
        dispatch({ type: "set-current-entry", entry });
        if (source === "navigate") {
          suppressNextPopState = true;
          window.dispatchEvent(new PopStateEvent("popstate", { state: null }));
        }
        return;
      }

      await unmountActiveApp();

      if (!entry) {
        dispatch({ type: "set-current-entry", entry: null });
        status = "mount-error";
        renderShellError(outlet, `No registered application for ${appPath}.`, status);
        return;
      }

      log?.("route-render:entry-resolved", {
        appId: entry.id,
        appPath
      });
      dispatch({ type: "set-current-entry", entry });
      status = "loading";

      const hostElement = renderAppHost(outlet, entry, status);
      const isolatedMountPoint = createIsolatedMountPoint(hostElement);
      pendingHostElement = hostElement;
      pendingStyles = await createRemoteStyleBoundary(entry, isolatedMountPoint.shadowRoot);
      log?.("route-render:style-boundary-ready", {
        appId: entry.id
      });
      registerRemote(entry);
      const appModule = await loadRemote<AppModule>(`${entry.id}/App`);
      log?.("route-render:remote-loaded", {
        appId: entry.id
      });

      if (!isAppModule(appModule)) {
        throw new Error(`Remote ${entry.id}/App did not expose mount and unmount.`);
      }

      await pendingStyles.attach();
      log?.("route-render:styles-attached", {
        appId: entry.id
      });
      const session = getSession();
      // entry.route stays app-relative (registry contract), but apps that hand it straight to a
      // router's `basename` (e.g. react-router's BrowserRouter) need it to match the real,
      // tenant-qualified browser URL — so recover the live prefix from the pathname instead of
      // trusting entry.route alone.
      const normalizedPathname = normalizePathname(window.location.pathname);
      const tenantPrefix = normalizedPathname.slice(0, normalizedPathname.length - appPath.length);
      const browserBasePath = `${tenantPrefix}${entry.route}`;
      await appModule.mount(
        isolatedMountPoint.container,
        createMountContext(
          entry,
          registry,
          {
            bus: session.bus,
            identity: session.identity,
            tenant: session.tenant,
            href,
            navigate: navigateTo,
            dialogController
          },
          browserBasePath,
          appPath,
          activeWorkspace,
          [...getWorkspaces()],
          getVariables()
        )
      );
      log?.("route-render:app-mounted", {
        appId: entry.id
      });
      status = "mounted";
      hostElement.dataset.appStatus = status;
      activeApp = {
        el: isolatedMountPoint.container,
        entry,
        workspaceId: resolvedWorkspace.workspaceId,
        hostElement,
        module: appModule,
        styles: pendingStyles,
        status
      };
      pendingHostElement = null;
      pendingStyles = null;
      log?.("route-render:complete", {
        appId: entry.id
      });
    } catch (error) {
      pendingStyles?.cleanup();
      pendingHostElement?.shadowRoot?.replaceChildren();
      status = "mount-error";
      const message = error instanceof Error ? error.message : "Application failed to mount.";
      renderShellError(outlet, message, status);
    }
  }

  function redirectToInitialRoute(route: string): void {
    const destination = getBrowserPath(route);

    // A root URL has no source app that can own its query or hash state.
    window.history.replaceState(window.history.state, "", destination);
    void scheduleRoute();
  }

  async function unmountActiveApp(): Promise<void> {
    if (!activeApp) {
      return;
    }

    const app = activeApp;
    status = "unmounting";
    app.status = status;
    app.hostElement.dataset.appStatus = status;
    await dialogController.closeDialogsForApp(app.entry.id);

    try {
      await app.module.unmount(app.el);
    } finally {
      app.styles.cleanup();
      app.hostElement.shadowRoot?.replaceChildren();
      dispatch({ type: "nav-clear" });
      activeApp = null;
      status = "not-loaded";
    }
  }

  return {
    href,
    navigateTo,
    handlePopState,
    start,
    clearActiveApp
  };
}
